import { useState } from 'react'
import React from 'react'
import { useAccount, useChainId, useReadContract } from 'wagmi'
import { useMint } from '@/hooks/useMint'
import { useContractInfo, useUserMintInfo } from '@/hooks/useContractInfo'
import { useCanMint } from '@/hooks/useUserNFTs'
import { getNFTContract } from '@/config/contract'
import { defaultChainId } from '@/config/wagmi'
import { formatEthDisplay } from '@/lib/utils'
import { Minus, Plus, Loader2, CheckCircle, AlertCircle, Wallet, Zap } from 'lucide-react'
import { formatEther } from 'viem'
import { motion } from 'framer-motion'

export const MintSection = () => {
  const { address, isConnected } = useAccount()
  const chainIdFromWallet = useChainId()
  const chainId = chainIdFromWallet || defaultChainId
  const contract = getNFTContract(chainId)
  const { data: contractInfo, isLoading: isLoadingContract, error: contractError } = useContractInfo()
  const { numberMinted } = useUserMintInfo(address)
  const [quantity, setQuantity] = useState(1)
  const { mint, isLoading, isConfirming, isSuccess, error } = useMint()
  const { canMint, remaining, reason } = useCanMint(address, quantity)
  
  // Direct test read to diagnose - simple single call
  const { data: testOwner, isLoading: isLoadingOwner, error: testError } = useReadContract({
    ...contract,
    functionName: 'owner',
    query: {
      enabled: !!contract.address && contract.address !== '0x',
      retry: 1,
    },
  })
  
  // Debug logging
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    
    console.log('🔍 MintSection Debug:', {
      chainId,
      contractAddress: contract.address,
      isConnected,
      isLoadingContract,
      contractError: contractError?.message,
      testOwner: testOwner ? String(testOwner) : null,
      isLoadingOwner,
      testError: testError?.message,
    })
    
    if (testError) {
      console.error('❌ Test read error:', {
        message: testError.message,
        cause: testError.cause,
        details: testError,
      })
    }
    
    if (testOwner) {
      console.log('✅ Test read SUCCESS! Owner:', String(testOwner))
    }
  }, [chainId, contract.address, isConnected, isLoadingContract, contractError, testOwner, isLoadingOwner, testError])

  if (isLoadingContract) {
    return (
      <div className="bg-[#111] border border-white/10 rounded-2xl p-8">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#d4a853]" />
          <p className="mt-3 text-white/50 text-sm">Loading...</p>
        </div>
      </div>
    )
  }

  if (!contractInfo) {
    return (
      <div className="bg-[#111] border border-white/10 rounded-2xl p-8">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto text-red-500" />
          <p className="mt-3 text-white font-medium">Контракт не подтягивается</p>
          <p className="mt-1 text-white/50 text-sm">Обычно после перезапуска ноды контракт нужно задеплоить заново</p>
          <div className="mt-4 p-4 bg-red-900/20 border border-red-700 rounded-lg text-left space-y-3">
            <p className="text-red-400 text-xs font-mono">Адрес: {contract.address || '—'} · Chain ID: {chainId}</p>
            {contractError && <p className="text-red-400 text-xs">Ошибка: {contractError.message}</p>}
            {testError && <p className="text-red-400 text-xs">Тест: {testError.message}</p>}
            <div className="pt-2 border-t border-red-800">
              <p className="text-amber-400 text-xs font-semibold mb-2">Что сделать:</p>
              <ol className="text-slate-300 text-xs space-y-1 list-decimal list-inside">
                <li>В отдельном терминале: <code className="text-green-400 bg-black/30 px-1 rounded">npx hardhat node</code></li>
                <li>В другом терминале: <code className="text-green-400 bg-black/30 px-1 rounded">npx hardhat run scripts/redeploy.js --network localhost</code></li>
                <li>Скрипт обновит <code className="text-amber-300">frontend/.env.local</code>. Перезапусти фронт и обнови страницу.</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const maxPerTx = Number(contractInfo.maxPerTx)
  const totalCost = BigInt(contractInfo.currentPrice) * BigInt(quantity)
  const totalCostFormatted = formatEthDisplay(formatEther(totalCost))

  const handleIncrement = () => {
    if (quantity < maxPerTx && quantity < remaining) {
      setQuantity(quantity + 1)
    }
  }

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1)
    }
  }

  const handleMint = () => {
    if (!canMint) return
    mint(quantity)
  }

  const isSoldOut = contractInfo.totalMinted >= contractInfo.maxSupply
  const isPaused = contractInfo.isPaused
  const mintingDisabled = !isConnected || isPaused || isSoldOut || !canMint

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      id="mint"
      className="scroll-mt-24"
    >
      <div className="bg-[#111] border border-white/10 rounded-2xl p-6 sm:p-8 max-w-lg mx-auto">
        {/* Header */}
        <h2 className="text-xl font-bold text-white text-center mb-6">
          Mint Your NFT
        </h2>

        {/* Status Messages */}
        {!isConnected && (
          <div className="mb-4 p-3 bg-[#d4a853]/10 border border-[#d4a853]/30 rounded-xl flex items-center gap-2">
            <Wallet className="w-4 h-4 text-[#d4a853]" />
            <p className="text-[#d4a853] text-sm">Connect wallet to mint</p>
          </div>
        )}

        {isPaused && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
            <p className="text-red-400 text-sm text-center">Minting paused</p>
          </div>
        )}

        {isSoldOut && (
          <div className="mb-4 p-3 bg-[#d4a853]/10 border border-[#d4a853]/30 rounded-xl">
            <p className="text-[#d4a853] font-bold text-center">SOLD OUT!</p>
          </div>
        )}

        {/* User Stats */}
        {isConnected && (
          <div className="mb-4 p-3 bg-white/5 rounded-xl flex justify-between text-sm">
            <span className="text-white/50">Your mints</span>
            <span className="text-white font-medium">
              {numberMinted.toString()}/{contractInfo.maxPerWallet.toString()}
            </span>
          </div>
        )}

        {/* Quantity Selector */}
        <div className="mb-4">
          <label className="block text-xs text-white/50 mb-2 uppercase tracking-wider">
            Quantity
          </label>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={handleDecrement}
              disabled={quantity <= 1 || isLoading || isConfirming}
              className="w-12 h-12 bg-white/5 hover:bg-white/10 disabled:opacity-30 rounded-xl flex items-center justify-center transition-colors border border-white/10"
            >
              <Minus className="w-4 h-4 text-white" />
            </button>
            
            <div className="w-20 h-12 bg-black rounded-xl flex items-center justify-center border border-[#d4a853]/30">
              <span className="text-2xl font-bold text-white">{quantity}</span>
            </div>
            
            <button
              onClick={handleIncrement}
              disabled={quantity >= maxPerTx || quantity >= remaining || isLoading || isConfirming}
              className="w-12 h-12 bg-white/5 hover:bg-white/10 disabled:opacity-30 rounded-xl flex items-center justify-center transition-colors border border-white/10"
            >
              <Plus className="w-4 h-4 text-white" />
            </button>
          </div>
          <p className="text-center text-xs text-white/30 mt-2">
            Max {Math.min(maxPerTx, remaining)} per tx
          </p>
        </div>

        {/* Total Cost */}
        <div className="mb-4 p-4 bg-[#d4a853]/5 border border-[#d4a853]/20 rounded-xl">
          <div className="flex justify-between items-center">
            <span className="text-white/60 text-sm">Total</span>
            <div>
              <span className="text-2xl font-bold text-white">{totalCostFormatted}</span>
              <span className="text-[#d4a853] ml-1 font-medium">ETH/BNB</span>
            </div>
          </div>
        </div>

        {/* Error/Warning Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
            <p className="text-red-400 text-xs text-center">{error}</p>
          </div>
        )}

        {!canMint && reason && (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
            <p className="text-yellow-400 text-xs text-center">{reason}</p>
          </div>
        )}

        {isSuccess && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <p className="text-green-400 text-sm">Minted successfully!</p>
          </div>
        )}

        {/* Mint Button */}
        <button
          onClick={handleMint}
          disabled={mintingDisabled || isLoading || isConfirming}
          className="w-full py-4 bg-[#d4a853] hover:bg-[#c49a4a] disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
        >
          {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
          {isConfirming && <Loader2 className="w-4 h-4 animate-spin" />}
          {isLoading ? 'Preparing...' :
           isConfirming ? 'Confirming...' :
           mintingDisabled ? (
             isSoldOut ? 'Sold Out' :
             isPaused ? 'Paused' :
             !isConnected ? 'Connect Wallet' : 'Cannot Mint'
           ) : (
             <>
               <Zap className="w-4 h-4" />
               Mint {quantity} NFT{quantity > 1 ? 's' : ''}
             </>
           )}
        </button>

        {/* Refund Info */}
        <p className="mt-4 text-center text-xs text-white/30">
          7-day refund guarantee • 90% money back
        </p>
      </div>
    </motion.div>
  )
}
